import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import DataMap from 'assets/components/dataMap/DataMap';
import RenderProps from 'assets/components/helpers/RenderProps';
import Icon from 'assets/components/icon/Icon';
import Media from 'assets/components/media/Media';
import { useTheme } from 'assets/hooks/theme/useTheme';
import { getLocales } from 'assets/locales/Locale';
import { preventDefaultBehavior } from 'assets/utils/dom/Events';
import { useState } from 'react';
import useFileInput from '../hooks/FileInput';
import { isEmpty } from 'lodash';
import ErrorWrapper from '../../wrappers/ErrorWrapper';

export enum SimpleFileInputStructure {
  wrapper = 'wrapper',
  input = 'input',
  buttonWrapper = 'buttonWrapper',
  simpleFileInputGallery = 'simpleFileInputGallery',
  simpleFileInputCard = 'simpleFileInputCard',
  simpleFileInputMedia = 'simpleFileInputMedia',
  simpleFileInputDelete = 'simpleFileInputDelete',
  simpleFileInputAdditionalInputs = 'simpleFileInputAdditionalInputs',
}
export type Verify = Utils.VerifyExtends<Component.Input.SimpleFile.Structure, typeof SimpleFileInputStructure>;
export default function SimpleFileInput(props: Component.Input.SimpleFile.Import) {
  const extend = useTheme(SimpleFileInputStructure, props);
  const { lang } = getLocales();
  const { fileInput, values, handleClick, handleFileSelect, removeFile, limit } = useFileInput(props);
  const [isShown, show] = useState<boolean>(false);

  return (
    <div {...extend(SimpleFileInputStructure.wrapper, true)}>
      <input
        {...extend(SimpleFileInputStructure.input)}
        onClick={(event) => event.stopPropagation()}
        onChange={handleFileSelect}
        type="file"
        ref={fileInput}
        name={props.name as string}
        accept={props.allowedMimeTypes ? props.allowedMimeTypes.join(', ') : '*'}
        multiple={props.fileLimit > 1 ? true : false}
      />
      <div {...extend(SimpleFileInputStructure.buttonWrapper)}>
        <Button
          class={ThemeButtonCircle}
          active
          disabled={props.disabled}
          onClick={handleClick}
          render={lang.upload}
          media={<Icon class="fas-upload" />}
        />
        <Button
          class={ThemeButtonCircle}
          disabled={props.disabled}
          onClick={() => show((prev) => !prev)}
          render={isShown ? lang.hide : lang.show}
          media={isShown ? <Icon class="fas-minus" /> : <Icon class="fas-plus" />}
        />
      </div>
      {isShown ? (
        !isEmpty(values) ? (
          <div
            {...extend(SimpleFileInputStructure.simpleFileInputGallery)}
            data-error={props.error || undefined}
            children={
              <>
                {props.label}
                <DataMap
                  data={values}
                  render={({ data: file, index }) => (
                    <div
                      {...extend(SimpleFileInputStructure.simpleFileInputCard)}
                      children={
                        <>
                          <div
                            {...extend(SimpleFileInputStructure.simpleFileInputMedia)}
                            key={index}
                            data-id={index}
                            onMouseDown={(event) => event.stopPropagation()}
                            title={file?.name ?? file.original?.name}
                            children={
                              <>
                                <Media
                                  useThumbnail
                                  media={{
                                    url: file.dataUrl,
                                    mime_type: file.type,
                                  }}
                                />
                                {props.actionBar ? (
                                  <>
                                    {RenderProps(props, 'actionBar', {
                                      file,
                                      remove: () => removeFile(index),
                                    })}
                                  </>
                                ) : (
                                  <>
                                    <div
                                      {...extend(SimpleFileInputStructure.simpleFileInputDelete)}
                                      onMouseDown={(event) => {
                                        preventDefaultBehavior(event);
                                        removeFile(index);
                                      }}
                                      children={<Icon class="ti-trash" />}
                                    />
                                  </>
                                )}
                              </>
                            }
                          />
                          <div
                            {...extend(SimpleFileInputStructure.simpleFileInputAdditionalInputs)}
                            children={RenderProps(props, 'additionalInputs', {
                              limit,
                              file,
                              index,
                            })}
                          />
                        </>
                      }
                    />
                  )}
                />
              </>
            }
          />
        ) : (
          <ErrorWrapper children={lang.noFiles} style={{ width: '100%' }} />
        )
      ) : null}
    </div>
  );
}
